import get from 'lodash/get';
import Cookies from 'js-cookie';
import { push } from 'connected-react-router';

import { MENU, MENU_ITEM } from 'bento-ordering/menus/constants/entities';
import { getBaseMenuPathForLocation } from 'bento-ordering/menus/selectors';
import { CART } from 'bento-ordering/cart/constants/entities';
import {
  MENU_HOURS_EXPIRED,
  SOLD_OUT_INVENTORY
} from 'bento-ordering/cart/constants/notificationTypes';

import { addToCart, updateCartItem } from 'bento-ordering/cart/actions';
import validateCart from 'bento-ordering/cart/actions/validateCart';
import { closeModal, openModal } from 'bento-ordering/base/actions/modal';
import { openNotificationModal } from 'bento-ordering/base/actions/notificationModals';
import { trackAddMenuUpsellItem } from 'bento-ordering/base/actions/tracking';
import { generatePath } from 'bento-ordering/utils/generatePath';
import { isMobileMedia } from 'bento-ordering/utils/media';
import { getOnlineOrderingUrlParams } from 'bento-ordering/utils/url';
import {
  areFulfillmentDeliveryOptionsSetSelector,
  getCartItemByIdSelector
} from 'bento-ordering/cart/selectors';
import { goToOrderSettingsViaSlug } from 'bento-ordering/locations/actions/locations';
import { getMenuItemEndpoint } from 'bento-ordering/base/constants/endpoints';
import { fetchSingleEntity } from 'bento-ordering/base/actions/singleEntity';
import {
  UPSELL,
  STANDARD
} from 'bento-ordering/cart/constants/conversionMethods';
import { OO_UPSELL_HEADER_AB } from 'constants/cookies';
import { PRODUCT_VARIANT_FIELD_NAME } from 'constants/menu/menuItemDetailForm';
import getGroupOrderQueryParam from 'bento-ordering/base/utils/getGroupOrderQueryParam';
import { buildFormModificationsForPricePreview } from 'helpers/forms/menuItem';
import { fetchLocationInventory } from 'bento-ordering/locations/actions/locationInventory';

export const fetchMenuItemEntity = menuItemSlug => (dispatch, getState) => {
  const state = getState();
  const endpoint = getMenuItemEndpoint(menuItemSlug);
  const queryParams = getGroupOrderQueryParam(state);

  dispatch(fetchSingleEntity(MENU_ITEM, endpoint, queryParams)).then(
    response => {
      if (response.payload.status === 404) {
        if (get(response.payload, 'response.error') === 'No Menu found.') {
          dispatch(openNotificationModal(MENU_HOURS_EXPIRED));
        } else {
          dispatch(menuItemModule.closeMenuItemAndGoToMenu());
        }
      }
    }
  );
};

const buildMenuItemFormPayload = (
  modifierGroups,
  values,
  productId,
  menuId,
  conversion_method = STANDARD
) => {
  const {
    quantityInput,
    'order-made-for': orderMadeFor,
    specialInstructions,
    [PRODUCT_VARIANT_FIELD_NAME]: productVariantId,
    ...modifications
  } = values;

  const payload = {
    fulfillment_menu_id: menuId,
    product_id: productId,
    quantity: quantityInput,
    order_made_for: orderMadeFor,
    special_instructions: specialInstructions,
    conversion_method: conversion_method,
    required_fields: {
      ...buildFormModificationsForPricePreview(modifierGroups, modifications)
    }
  };

  if (productVariantId) {
    payload.variant_id = parseInt(productVariantId);
  }

  return payload;
};

export const handleFormItemSubmit = (values, props) => dispatch => {
  dispatch(handleFormSubmit(values, props));
};

export const handleFormItemUpdate = (values, props) => dispatch => {
  const cartItemId = props.match.params.cartItemId;
  dispatch(handleFormUpdate(cartItemId, values, props.modifierGroups));
};

/**
 * Ensures modifiers are ordered correctly rathen than
 * based on which order the customer selects them
 */
export const getOrderedValues = (values, modifierGroups) => {
  const orderedValues = {};
  const selectedValues = Object.keys(values);

  modifierGroups.forEach(modifier => {
    const { slug } = modifier;
    const modifierIsInValuesArray = selectedValues.includes(slug);
    if (modifierIsInValuesArray) {
      orderedValues[slug] = values[slug];
    }
  });

  // catches any non-modifer values like special instructions
  selectedValues.forEach(value => {
    if (!orderedValues[value]) {
      orderedValues[value] = values[value];
    }
  });

  return orderedValues;
};

/**
 * Action to update the an existing cart item
 */
export const handleFormUpdate = (cartItemId, values, modifierGroups) => async (
  dispatch,
  getState
) => {
  const { cart, menu } = getState();
  const id = parseInt(cartItemId);
  const cartItemObject = getCartItemByIdSelector(cart, id);
  const productId = cartItemObject.product;

  const orderedValues = getOrderedValues(values, modifierGroups);

  const payload = menuItemModule.buildMenuItemFormPayload(
    modifierGroups,
    orderedValues,
    productId,
    menu.id
  );
  const result = await dispatch(updateCartItem(id, payload));

  if (!get(result, ['payload', 'response', 'error'], '')) {
    dispatch(menuItemModule.returnToCart());
  }
};

/**
 * Action to add new menu item to cart
 */
export const handleFormSubmit = (values, props) => async (
  dispatch,
  getState
) => {
  const { conversion_method, productId, modifierGroups } = props;
  const orderedValues = getOrderedValues(values, modifierGroups);

  const { menu } = getState();

  const payload = menuItemModule.buildMenuItemFormPayload(
    modifierGroups,
    orderedValues,
    productId,
    menu.id,
    conversion_method
  );

  await dispatch(addToCart(payload));
  conversion_method === UPSELL
    ? dispatch(executeUpsellNextSteps())
    : dispatch(goToNextStepAfterAdd());
};

export const handleQuickAddToCart = productId => async (dispatch, getState) => {
  const conversionMethod = UPSELL;

  const { menu } = getState();

  const payload = {
    fulfillment_menu_id: menu.id,
    product_id: productId,
    quantity: 1,
    order_made_for: '',
    special_instructions: '',
    conversion_method: conversionMethod,
    required_fields: {}
  };

  const result = await dispatch(addToCart(payload));

  if (result.payload.success) {
    const isUpsellHeaderABEnabled = Cookies.get(OO_UPSELL_HEADER_AB) === 'True';
    trackAddMenuUpsellItem(isUpsellHeaderABEnabled);
    dispatch(validateCart());
  } else if (result.payload.response?.error_type === SOLD_OUT_INVENTORY) {
    const {
      cart: { online_ordering_location: selectedLocation }
    } = getState();
    dispatch(fetchLocationInventory(selectedLocation));
  }
};

const executeUpsellNextSteps = () => dispatch => {
  const isUpsellHeaderABEnabled = Cookies.get(OO_UPSELL_HEADER_AB) === 'True';
  trackAddMenuUpsellItem(isUpsellHeaderABEnabled);
  dispatch(returnToCart());
};

const goToNextStepAfterAdd = () => (dispatch, getState) => {
  const { cart } = getState();
  const areFulfillmentOptionsSet = areFulfillmentDeliveryOptionsSetSelector(
    cart
  );
  if (areFulfillmentOptionsSet) {
    return dispatch(menuItemModule.closeMenuItemAndGoToMenu());
  }

  dispatch(closeMenuItemAndGoToOrderSettings());
};

const closeMenuItemAndGoToOrderSettings = () => (dispatch, getState) => {
  const { router } = getState();
  const { locationSlug } = getOnlineOrderingUrlParams(router.location.pathname);
  dispatch(goToOrderSettingsViaSlug(locationSlug));
};

export const returnToCart = () => dispatch => {
  dispatch(push(generatePath(CART, 'cart')));
};

export const closeMenuItemAndGoToMenu = () => (dispatch, getState) => {
  dispatch(closeModal());
  const path = getBaseMenuPathForLocation(getState());
  dispatch(push(path));
};

export const navigateToMenuItem = slug => (dispatch, getState) => {
  const state = getState();

  if (!isMobileMedia()) {
    dispatch(openModal());
  }

  const { locationSlug } = getOnlineOrderingUrlParams(
    state.router.location.pathname
  );

  dispatch(
    push(
      generatePath(MENU, 'menuItemDetail', {
        slug: locationSlug,
        menuItemSlug: slug
      })
    )
  );
};

// package this module for export.
// any method defined here that uses one of these methods
// should use the version attached to menuItemModule
//  this makes it simple to mock certain pieces of the module
// for tests
const menuItemModule = {
  handleFormItemUpdate,
  handleFormUpdate,
  updateCartItem,
  returnToCart,
  buildMenuItemFormPayload,
  fetchMenuItemEntity,
  closeMenuItemAndGoToMenu,
  handleFormSubmit,
  getOrderedValues,
  handleQuickAddToCart
};

export default menuItemModule;
